import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addCompanySchema, type AddCompanyForm } from "@kkp/features";
import { toast } from "@kkp/ui";
import {
  createCompany,
  type Company,
  type CreateCompanyResponse,
} from "../../../api/companies";

export function notifyCompanyCreated(
  created: CreateCompanyResponse,
  picEmail: string,
) {
  if (created.inviteSent) {
    toast.success(`Invite email sent to ${picEmail}.`, {
      title: "Company created",
      durationMs: 2400,
    });
  } else {
    toast.warning(
      created.warning ??
        "The invite email was not sent; use Resend Invite on the company page.",
      { title: "Company created, invite not sent", durationMs: 4000 },
    );
  }
}

/**
 * Add-company dialog form: owns the dialog open state + react-hook-form wiring +
 * the create submission. On success it hands the created company back via
 * `onCreated` (the data hook appends it) and closes/resets itself. Admin-specific:
 * numeric coercion of the limit/fee fields + the invite-warning-aware toast.
 */
export function useAddCompanyForm({
  onCreated,
}: {
  onCreated: (company: Company) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddCompanyForm>({
    resolver: zodResolver(addCompanySchema),
    defaultValues: {
      legalName: "",
      backOfficeId: "",
      taxId: "",
      legalAddress: "",
      sourceBankCode: "",
      sourceBankAccount: "",
      perBatchLimitIdr: "",
      perTransactionFeeIdr: "",
      picFullname: "",
      picPhonenumber: "",
      picEmail: "",
      reasonDescription: "",
    },
  });

  const onSubmit = handleSubmit(async (formValues) => {
    try {
      const newCompany = await createCompany({
        companyLegalName: formValues.legalName,
        backOfficeId: formValues.backOfficeId,
        taxId: formValues.taxId,
        legalAddress: formValues.legalAddress,
        sourceBankCode: formValues.sourceBankCode,
        sourceBankAccount: formValues.sourceBankAccount,
        perBatchLimitIdr: formValues.perBatchLimitIdr,
        perTransactionFeeIdr: formValues.perTransactionFeeIdr,
        picFullname: formValues.picFullname,
        picPhonenumber: formValues.picPhonenumber,
        picEmail: formValues.picEmail,
        reasonDescription: formValues.reasonDescription,
      });
      const merged: Company = { ...newCompany, status: "Active" };
      onCreated(merged);
      notifyCompanyCreated(newCompany, formValues.picEmail);
      setIsOpen(false);
      reset();
    } catch (err) {
      toast.danger("Failed to create company.", { title: "Error" });
      console.error(err);
    }
  });

  return { isOpen, setIsOpen, register, errors, isSubmitting, onSubmit };
}
